import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Search, MapPin, Code, Award, Sparkles } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { landingPages } from "@/data/landingPages";

const ALLOWED_HUB_TABS = new Set(["services", "industries", "regions", "tools"]);

const TAB_ROWS = [
  {
    id: "services",
    label: "Services",
    Icon: Code,
    testId: "specialized-group-services",
    countSelector: "service",
  },
  {
    id: "industries",
    label: "Industries",
    Icon: Award,
    testId: "specialized-group-industries",
    countSelector: "industry",
  },
  {
    id: "regions",
    label: "Regions",
    Icon: MapPin,
    testId: "specialized-group-locations",
    countSelector: "location",
  },
];

function compileLists() {
  const seoPages = landingPages.filter((p) => p.pageType === "seo");
  const cmpPages = landingPages.filter((p) => p.pageType === "campaign");
  const sortByLabel = (a, b) =>
    String(a.shortTitle || a.title).localeCompare(
      String(b.shortTitle || b.title),
      undefined,
      { sensitivity: "base" },
    );
  const bucket = (cat) =>
    seoPages.filter((p) => p.category === cat).sort(sortByLabel);
  return {
    service: bucket("service"),
    industry: bucket("industry"),
    location: bucket("location"),
    campaign: cmpPages.sort(sortByLabel),
  };
}

function filterPages(pages, qRaw) {
  const q = qRaw.trim().toLowerCase();
  if (!q) return pages;
  return pages.filter((p) => {
    const hay = [p.title, p.shortTitle, p.targetKeyword, p.heroEyebrow]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}

function HubLinkCard({ page }) {
  const label = page.shortTitle || page.title;
  return (
    <Link
      to={page.path}
      data-testid={`specialized-hub-link-${page.slug}`}
      className={cn(
        "group relative flex flex-col rounded-xl border border-white/8 bg-[#0f0f0f]/90 px-3.5 py-3 transition-all duration-300",
        "hover:border-[#00AEEF]/45 hover:bg-[#00AEEF]/[0.06] hover:shadow-[0_0_24px_rgba(0,174,239,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00AEEF]/50",
      )}
    >
      <span className="flex items-start justify-between gap-2">
        <span className="text-[13px] font-medium text-white leading-snug group-hover:text-[#00AEEF] transition-colors">
          {label}
        </span>
        <ArrowRight className="w-3.5 h-3.5 text-gray-600 group-hover:text-[#00AEEF] shrink-0 mt-0.5 transition-colors" />
      </span>
      {page.heroEyebrow && (
        <span className="mt-1 text-[10px] uppercase tracking-wide text-gray-600 group-hover:text-gray-500">
          {page.heroEyebrow}
        </span>
      )}
    </Link>
  );
}

function LinkGrid({ pages, query, variantKey }) {
  const visible = useMemo(() => filterPages(pages, query), [pages, query]);
  return (
    <>
      <div className="px-4 py-4 md:px-6 border-b border-white/5 bg-[#161616]/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xs text-gray-500">
          Showing{" "}
          <strong className="text-gray-400 tabular-nums">{visible.length}</strong>{" "}
          {visible.length === 1 ? "page" : "pages"}
        </p>
        <Link
          to="/services/mobile-app-development"
          className="text-xs font-medium text-[#00AEEF] hover:underline shrink-0"
        >
          View core services overview →
        </Link>
      </div>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={`${variantKey}-${visible.length}-${query.trim()}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.28, ease: "easeOut" }}
          className="relative max-h-[min(56vh,480px)] overflow-y-auto px-4 py-4 md:px-6 md:py-6 [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/10 [&::-webkit-scrollbar]:w-1.5"
        >
          {visible.length > 0 ? (
            <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5">
              {visible.map((page, i) => (
                <motion.li
                  key={page.slug}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.25,
                    delay: Math.min(i * 0.012, 0.36),
                    ease: "easeOut",
                  }}
                >
                  <HubLinkCard page={page} />
                </motion.li>
              ))}
            </ul>
          ) : (
            <p className="text-center py-14 text-gray-500 text-sm">
              No matches. Try another keyword or clear the search.
            </p>
          )}
        </motion.div>
      </AnimatePresence>
    </>
  );
}

export default function SpecializedSolutionsHub() {
  const lists = useMemo(() => compileLists(), []);
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState("services");
  const [query, setQuery] = useState("");

  useEffect(() => {
    const raw = searchParams.get("browseHub");
    const v = typeof raw === "string" ? raw.trim().toLowerCase() : "";
    if (!v || !ALLOWED_HUB_TABS.has(v)) return;
    setTab(v);
    setQuery("");
  }, [searchParams]);

  return (
    <section
      id="browse-solutions"
      data-testid="specialized-solutions-section"
      className="py-20 md:py-32 bg-[#0f0f0f] border-t border-white/5 scroll-mt-[5.5rem]"
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          className="mb-10 md:mb-14 max-w-3xl"
        >
          <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-3">
            Specialized Solutions
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight">
            Explore by focus
          </h2>
          <p className="text-gray-400 text-base leading-relaxed mt-4">
            Switch categories, search in place, and open deep-dive guides for
            platforms, industries, metros, and free tools, without endless
            scrolling.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: "easeOut", delay: 0.05 }}
          className="relative"
        >
          <div
            className="absolute -inset-4 rounded-3xl bg-[#00AEEF]/10 blur-2xl pointer-events-none opacity-70"
            aria-hidden
          />
          <Tabs
            value={tab}
            onValueChange={(v) => {
              setTab(v);
              setQuery("");
            }}
            className="relative rounded-2xl border border-white/10 bg-[#121212] shadow-[0_24px_80px_rgba(0,0,0,0.45)] overflow-hidden"
          >
            <div className="border-b border-white/8 bg-gradient-to-br from-[#1a1a1a]/90 to-[#0f0f0f]/90 px-4 py-5 md:px-6 md:py-6">
              <TabsList
                data-testid="specialized-hub-tabs"
                className="flex flex-wrap gap-2 p-1.5 rounded-xl bg-black/55 border border-white/10 shadow-inner w-full md:w-auto md:inline-flex justify-start md:justify-center h-auto"
              >
                {TAB_ROWS.map(({ id, label, Icon, testId, countSelector }) => (
                  <TabsTrigger
                    key={id}
                    value={id}
                    data-testid={`${testId}-tab`}
                    className={cn(
                      "flex items-center gap-2 rounded-lg px-3 py-2.5 text-[13px] font-medium whitespace-nowrap shrink-0",
                      "border border-transparent data-[state=active]:border-[#00AEEF]/40 data-[state=active]:bg-[#00AEEF]/14 data-[state=active]:text-white data-[state=active]:shadow-[0_0_20px_rgba(0,174,239,0.12)]",
                      "text-gray-400 hover:text-gray-200 transition-colors",
                    )}
                  >
                    <Icon className="w-4 h-4 text-[#00AEEF]" />
                    <span>{label}</span>
                    <span className="ml-1 rounded-full bg-white/10 px-1.5 py-0 text-[10px] font-semibold text-gray-300 tabular-nums">
                      {lists[countSelector].length}
                    </span>
                  </TabsTrigger>
                ))}
                <TabsTrigger
                  value="tools"
                  data-testid="specialized-group-resources-tab"
                  className={cn(
                    "flex items-center gap-2 rounded-lg px-3 py-2.5 text-[13px] font-medium whitespace-nowrap shrink-0",
                    "border border-transparent data-[state=active]:border-violet-500/40 data-[state=active]:bg-violet-500/14 data-[state=active]:text-white data-[state=active]:shadow-[0_0_18px_rgba(139,92,246,0.15)]",
                    "text-gray-400 hover:text-gray-200 transition-colors",
                  )}
                >
                  <Sparkles className="w-4 h-4 text-violet-400" />
                  <span>Free tools</span>
                  <span className="ml-1 rounded-full bg-white/10 px-1.5 py-0 text-[10px] font-semibold text-gray-300 tabular-nums">
                    {lists.campaign.length}
                  </span>
                </TabsTrigger>
              </TabsList>

              <label className="mt-6 flex items-center gap-3 rounded-xl border border-white/10 bg-black/35 px-3.5 py-3 focus-within:border-[#00AEEF]/40 transition-colors cursor-text shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                <Search className="w-4 h-4 text-[#00AEEF] shrink-0" aria-hidden />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Filter this list..."
                  aria-label="Filter landing page list"
                  data-testid="specialized-hub-search"
                  className="flex-1 bg-transparent border-0 p-0 text-sm text-gray-100 placeholder:text-gray-600 focus-visible:outline-none min-w-0"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="text-[11px] text-gray-500 hover:text-[#00AEEF] whitespace-nowrap"
                  >
                    Clear
                  </button>
                )}
              </label>
            </div>

            {TAB_ROWS.map(({ id, testId }) => (
              <TabsContent key={id} value={id} className="m-0">
                <div data-testid={testId}>
                  <LinkGrid
                    variantKey={id}
                    pages={
                      id === "services"
                        ? lists.service
                        : id === "industries"
                          ? lists.industry
                          : lists.location
                    }
                    query={query}
                  />
                </div>
              </TabsContent>
            ))}

            <TabsContent value="tools" className="m-0">
              <div data-testid="specialized-group-resources">
                <LinkGrid variantKey="tools" pages={lists.campaign} query={query} />
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </section>
  );
}
